import { useEffect, useRef, type FC } from 'react';
import type { OptionalExchangeRatesCurrentRequest } from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type { AuctionReport } from 'src/apis/monolith/types';
import type { ActivitiesRequest } from 'src/apis/monolith/use-activities';
import type { AuctionsRequest } from 'src/apis/monolith/use-auctions';
import { TableActions } from 'src/components/buttons-and-actions/table-actions';
import { FormatDate } from 'src/components/data-display/format-date';
import { FormatValue } from 'src/components/data-display/format-value';
import { ExpandButton, Table } from 'src/components/data-display/smart-table';
import { Td, Th } from 'src/components/data-display/table';
import { Spacer } from 'src/components/layout/spacer';
import { Link } from 'src/components/navigation/link';
import { useScrollManager } from 'src/components/scroll-management';
import { Heading } from 'src/components/text/heading';
import type { Successful } from 'src/hooks/use-axios';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import type { SavedReferrer } from 'src/hooks/use-referrer';
import { PeriodType } from 'src/pages/reporting/auctions/components/period-type';
import { Capacity } from 'src/pages/reporting/capacity';
import { createBidHeaderId } from 'src/pages/transport/auctions/components/activities';
import { AuctionStatusDisplay } from 'src/pages/transport/auctions/components/status';
import { useAuctionCountdown } from 'src/pages/transport/auctions/details/use-auction-countdown';
import { EmbeddedShortTermBiddingForm } from 'src/pages/transport/auctions/overview/embedded-short-term-bidding-form';
import {
  PriceInEURMWhTd,
  PriceInEURMWhTh,
} from 'src/pages/transport/auctions/overview/price-in-eur-mwh-col';
import type { NwpDetailParams } from 'src/pages/transport/network-points/details/point-details/use-page-params';

type AuctionsNowProps = {
  pageParams: NwpDetailParams;
  longTermAuctions: Successful<AuctionsRequest>;
  shortTermAuctions: Successful<AuctionsRequest>;
  savedReferrer: SavedReferrer;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  activities?: Successful<ActivitiesRequest>;
};

export const AuctionsNow: FC<AuctionsNowProps> = ({
  pageParams,
  longTermAuctions,
  shortTermAuctions,
  savedReferrer,
  exchangeRates,
  activities,
}) => {
  return (
    <div className="grid gap-md">
      <Heading mode="headline" size="md" id="auctions-now">
        Auctions Now at This Point
      </Heading>

      <div className="grid gap-md">
        <Heading mode="title" size="sm">
          Long Term
        </Heading>

        <AuctionsTable
          isLongTerm
          auctions={longTermAuctions}
          pageParams={pageParams}
          savedReferrer={savedReferrer}
          exchangeRates={exchangeRates}
          activities={activities}
        />
      </div>

      <Spacer />

      <div className="grid gap-md">
        <Heading mode="title" size="sm">
          Short Term
        </Heading>

        <AuctionsTable
          auctions={shortTermAuctions}
          pageParams={pageParams}
          savedReferrer={savedReferrer}
          exchangeRates={exchangeRates}
          activities={activities}
        />
      </div>
    </div>
  );
};

type AuctionsTableProps = {
  pageParams: NwpDetailParams;
  auctions: Successful<AuctionsRequest>;
  isLongTerm?: boolean;
  savedReferrer: SavedReferrer;
  exchangeRates: OptionalExchangeRatesCurrentRequest;
  activities?: Successful<ActivitiesRequest>;
};

const AuctionsTable: FC<AuctionsTableProps> = ({
  pageParams,
  auctions,
  isLongTerm = false,
  savedReferrer,
  exchangeRates,
  activities,
}) => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const { scrollTo } = useScrollManager();
  const scrollToRef = useRef<string | null>(null);

  // whenever activities have been reloaded we check, if we need to scroll to a new bid
  useEffect(() => {
    if (!scrollToRef.current) return;
    scrollTo(scrollToRef.current, 'smooth');
    scrollToRef.current = null;
    // oxlint-disable-next-line exhaustive-deps
  }, [activities?.response]);

  return (
    <Table
      pending={auctions.pending}
      paginated={{
        perPage: isLongTerm
          ? pageParams.longTermAuctions.value.pageSize
          : pageParams.shortTermAuctions.value.pageSize,
        paramPrefix: isLongTerm ? 'longTermAuctions' : 'shortTermAuctions',
        ...auctions.response.data,
      }}
      data={auctions.response.data.data}
      cols={[
        // Bidding Form at Short Term Auctions Table
        !isLongTerm &&
          monolithUser?.isShipper && {
            width: '2rem',
            key: 'expandableRow',
            head: <Th />,
            body: (data, _, metaHandlers) => {
              return (
                <Td mode="expand-row">
                  {data.actions.includes('BID') && !data.suspended ? (
                    <ExpandButton
                      hideLabel="Hide bidding form"
                      showLabel="Show bidding form"
                      data-testid={`expandable-id-${data.id}`}
                      {...metaHandlers}
                    />
                  ) : null}
                </Td>
              );
            },
          },
        {
          key: 'capacityCategory',
          head: <Th>Category</Th>,
          body: (data) => (
            <Td
              data-testid={`capacityCategory-${data.id}`}
              id={createAuctionRowId(data)}
            >
              <Capacity value={data.capacityCategory0} />
              {data.networkPoint.bundled && data.capacityCategory1 && (
                <>
                  {' / '}
                  <Capacity value={data.capacityCategory1} />
                </>
              )}
            </Td>
          ),
        },
        {
          key: 'periodType',
          width: '6rem',
          head: <Th>Product</Th>,
          body: (data) => (
            <Td align="center" data-testid={`periodType-${data.id}`}>
              <PeriodType value={data.periodType} />
            </Td>
          ),
        },
        {
          key: 'runtime',
          width: '13rem',
          head: <Th>Runtime</Th>,
          body: (data) => (
            <Td data-testid={`runtime-${data.id}`}>
              <FormatDate value={data.runtime.start} type="date-time" />
              {' - '}
              <FormatDate value={data.runtime.end} type="date-time" />
            </Td>
          ),
        },
        {
          key: 'amount',
          head: <Th>Amount</Th>,
          body: (data) => (
            <Td data-testid={`amount-${data.id}`} align="right">
              <FormatValue
                value={data.marketable.value.value}
                label={data.marketable.unit}
                smallLabel
                stacked
              />
            </Td>
          ),
        },
        {
          width: 2,
          key: 'startingPrice',
          head: <Th>Starting Price</Th>,
          body: (data) => (
            <Td data-testid={`startingPrice-${data.id}`} align="right">
              <FormatValue
                type="4-decimals"
                value={data.startingPrice[0].value.value}
                label={data.startingPrice[0].unit.label}
                smallLabel
                stacked
              />
              {data.startingPrice[1] && (
                <>
                  {' '}
                  <em>or</em>
                  <br />
                  <FormatValue
                    type="4-decimals"
                    value={data.startingPrice[1].value.value}
                    label={data.startingPrice[1].unit.label}
                    smallLabel
                    stacked
                  />
                </>
              )}
            </Td>
          ),
        },
        {
          key: 'startingPriceEURMWh',
          width: 2,
          head: <PriceInEURMWhTh />,
          body: (data) => (
            <PriceInEURMWhTd auction={data} exchangeRates={exchangeRates} />
          ),
        },
        {
          width: 2,
          key: 'status',
          head: <Th>Status</Th>,
          body: (data) => (
            <Td data-testid={`status-${data.id}`}>
              <AuctionStatusWithCountdown auction={data} />
            </Td>
          ),
        },
        {
          width: '7rem',
          key: 'actions',
          head: <Th align="center">Actions</Th>,
          body: (data) => (
            <Td align="center" data-testid={`actions-${data.id}`}>
              <TableActions>
                <Link
                  {...savedReferrer.withReferrerLinkProps(
                    `/transport/auctions/${data.id}`
                  )}
                >
                  {/* Navigate to the Auction Details Page for Long and Short Term Auctions */}
                  {data.actions.includes('BID') && !data.suspended
                    ? 'Bid'
                    : 'Details'}
                </Link>
              </TableActions>
            </Td>
          ),
        },
      ]}
      meta={(data, _, { close }) => (
        <EmbeddedShortTermBiddingForm
          exchangeRates={exchangeRates}
          auctionId={data.id.toString()}
          onCancel={() => {
            close();
            scrollTo(`#${createAuctionRowId(data)}`, {
              offset: -100, // table header should not cover row
              behaviour: 'instant',
            });
          }}
          onSuccess={() => {
            close();
            scrollToRef.current = `#${createBidHeaderId({ auctionId: data.id })}`;
            auctions.refresh?.();
            activities?.refresh?.();
          }}
        />
      )}
      empty={
        isLongTerm
          ? {
              title: 'No Long Term Auctions',
              description: 'There are no long term auctions now at this point.',
            }
          : {
              title: 'No Short Term Auctions',
              description:
                'There are no short term auctions now at this point.',
            }
      }
    />
  );
};

export const AuctionStatusWithCountdown: FC<{ auction: AuctionReport }> = ({
  auction,
}) => {
  const countdown = useAuctionCountdown(auction);
  const isLongTerm =
    auction.periodType === 'MONTH' ||
    auction.periodType === 'QUARTER' ||
    auction.periodType === 'YEAR';
  const round = auction.round &&
    isLongTerm &&
    (auction.phase === 'OPEN' || auction.phase === 'CLOSED') && (
      <> (Round: {auction.round})</>
    );
  return (
    <AuctionStatusDisplay
      phase={auction.phase}
      suspended={auction.suspended}
      runningAuctions
      appendix={
        (countdown || round) && (
          <>
            {countdown}
            {round}
          </>
        )
      }
    />
  );
};

// used as a scroll anchor
const createAuctionRowId = ({ id }: AuctionReport) => `auction-row-${id}`;
